import type { PointerSpot } from './pointer'

const DROP_OVER = 'data-drop-over'
const GHOST_NUDGE = 14

// Measuring every month in the map on each pointer move would be thousands of rectangles on a
// long file, so the browser's own hit test says what the pages are held over.
export const dropTargetUnder = (spot: PointerSpot) =>
  document
    .elementFromPoint(spot.x, spot.y)
    ?.closest<HTMLElement>('[data-drop-target]') ?? null

// Marked straight on the node. Re-rendering a map of thousands of months on every pointer move
// is what makes a drag feel heavy.
export const markDropTarget = (
  previous: HTMLElement | null,
  next: HTMLElement | null,
) => {
  if (previous === next) return next
  previous?.removeAttribute(DROP_OVER)
  next?.setAttribute(DROP_OVER, '')
  return next
}

const INSERT_AFTER = 'data-insert-after'
const INSERT_GAP_PX = 2

// Where on a band the pages would land: after the last page whose middle the pointer has passed. The
// line is placed with a CSS variable so a drag never re-renders the strip.
export const markInsertPoint = (
  band: HTMLElement | null,
  spot: PointerSpot,
) => {
  if (!band?.hasAttribute('data-band-drop')) return
  const cards = [...band.querySelectorAll<HTMLElement>('[data-page-number]')]
  const passed = cards.filter((card) => {
    const box = card.getBoundingClientRect()
    return box.left + box.width / 2 < spot.x
  })
  const after = passed[passed.length - 1]
  const edge = after
    ? after.getBoundingClientRect().right + INSERT_GAP_PX
    : (cards[0]?.getBoundingClientRect().left ?? 0) - INSERT_GAP_PX
  band.setAttribute(INSERT_AFTER, after?.dataset.pageNumber ?? '')
  const row = cards[0]?.parentElement ?? band
  band.style.setProperty(
    '--insert-x',
    `${edge - row.getBoundingClientRect().left}px`,
  )
}

export const insertAfterOf = (band: HTMLElement) => {
  const page = band.getAttribute(INSERT_AFTER)
  return page ? Number(page) : null
}

export const ghostTransform = (spot: PointerSpot) =>
  `translate(${spot.x + GHOST_NUDGE}px, ${spot.y + GHOST_NUDGE}px)`

export const moveGhostTo = (
  node: HTMLElement | null,
  spot: PointerSpot,
): void => {
  if (node) node.style.transform = ghostTransform(spot)
}
